import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
// MUI
import {
  Grid2,
  TextField,
  IconButton as MuiIconButton,
  Tooltip,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
// styles
import styles from "./BlueprintCard.module.scss";
// Icons
import { ReactComponent as Delete } from "../../../../../../GlobalAssets/svg/Delete.svg";
import { ReactComponent as CreateSiteSvg } from "../../../../../../GlobalAssets/svg/CreateSite.svg";
import { ReactComponent as BlueprintSite } from "../../../../../../GlobalAssets/svg/Blueprint12.svg";
// Local import
import IconButton from "../../../../../../GlobalComponents/IconButton/IconButton";
import { Actions } from "../../../../../../redux/constants/actionTypes";
import { getStore } from "../../../../../../Utils/storeUtils";
import { useNavigate } from "react-router-dom";
import NewPopup from "../../../../../../GlobalComponents/Popup/NewPopup";
import ImageUploader from "../../../../../../GlobalComponents/ImageUploader/ImageUploader";
import { useSelector } from "react-redux";
import FormPopup from "../../../../../../GlobalComponents/FormPopup/FormPopup";
import { ActivatePopup } from "../../../../../../Utils/activatePopup";

export default function BlueprintCard(props) {
  // props and hooks
  const {
    blueprintName,
    imagePath,
    name,
    deleteBlueprint,
    orgId,
    blueprintId,
    usedCounter,
    isEditing,
    onEditClick,
    loading,
    setLoading,
    image,
    setImage,
    values,
    setValues,
    setUploadFile,
    isUpload,
    setIsUpload,
    fileInputRef,
    loadingDel,
    apiDeleteImage,
    apiImageReUpload,
    isPopupOpen,
    setIsPopupOpen,
    selectedOrg,
    dispatchGetAllBlueprint,
    numSitesFeature,
    reachedMaximum,
    creationInProgress,
  } = props;

  const navigate = useNavigate();
  const [editName, setEditName] = useState(blueprintName);
  const [isHovered, setIsHovered] = useState(false);
  const [editNameError, setEditNameError] = useState("");
  const [validationError, setValidationError] = useState("");
  const minLength = 3;
  const maxLength = 24;
  const textFieldRef = useRef(null);
  const [popup, setPopup] = useState({
    popupState: false,
    popupType: "",
    popupTitle: "",
    popupSubtitle: "",
    popupSubtitle1: "",
    popupButton: false,
    popupCancel: false,
    popupButtonTitle: "",
    popupCancelTitle: "",
    popupButtonFunction: () => {},
    popupCancelFunction: () => {},
  });

  const userDetails = useSelector((state) => state.users?.userDetails);

  const isLimitReached =
    numSitesFeature?.limitOrAvailablity === numSitesFeature?.used;

  const handleCreateSiteClick = () => {
    if (creationInProgress) {
      ActivatePopup("blueprint-creation-in-progress", setPopup);
      return;
    }
    if (isLimitReached) {
      setPopup({
        popupState: true,
        popupType: "warning",
        popupTitle: `${reachedMaximum?.title} ${numSitesFeature?.used}/${numSitesFeature?.limitOrAvailablity}`,
        popupSubtitle: reachedMaximum?.subTitle,
        popupCancel: true,
        popupButton: true,
        popupButtonTitle: reachedMaximum?.button,
        popupCancelTitle: "Cancel",
        popupButtonFunction: () => {
          setPopup({ popupState: false });
          navigate(`/account/${orgId}/modify-plan`);
        },
        popupCancelFunction: () => {
          setPopup({ popupState: false });
        },
      });
    } else {
      navigate(
        `/account/${
          selectedOrg?.orgId ? selectedOrg?.orgId : -1
        }/create?blueprintId=${blueprintId}`,
      );
    }
  };

  const renameBluePrint = () => {
    getStore().dispatch({
      type: Actions.API_RENAME_BLUEPRINT,
      data: {
        blueprintId: blueprintId,
        newBlueprintName: editName,
        orgId: orgId,
      },
      meta: {
        onSuccess: () => {
          setLoading(false);
          dispatchGetAllBlueprint();
        },
        onError: () => {
          setLoading(false);
          dispatchGetAllBlueprint();
        },
      },
    });
  };

  useEffect(() => {
    if (editName !== blueprintName) {
      getStore().dispatch({
        type: Actions.API_IS_VALID_BLUEPRINT_NAME,
        data: {
          blueprintName: editName,
          orgId: orgId,
        },
        meta: {
          onSuccess: (response) => {
            setValidationError(
              response === false ? "The name already exists!" : "",
            );
          },
          onError: () => {
            setValidationError(
              "Error validating blueprint name. Please try again.",
            );
          },
        },
      });
    } else {
      setValidationError("");
    }
  }, [editName, orgId]);

  const handleSaveClick = () => {
    if (editName !== blueprintName && !editNameError && !validationError) {
      renameBluePrint();
    }
    onEditClick(null);
  };

  const handleFileChange = (uploadImage) => {
    const [file] = uploadImage.target.files;
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setValues({ ...values, image: file });
      setUploadFile(file);
      setImage(fileURL);
      setIsUpload(false);
    }
  };

  const handleEditNameChange = (event) => {
    const value = event.target.value;
    setEditName(value);

    if (value.length > maxLength) {
      setEditNameError(`Name must not exceed ${maxLength} characters.`);
    } else if (value.length < minLength) {
      setEditNameError(`Name must be at least ${minLength} characters.`);
    } else {
      setEditNameError("");
    }
  };

  const handleClickOutside = (event) => {
    if (textFieldRef.current && !textFieldRef.current.contains(event.target)) {
      if (editName !== blueprintName && !editNameError && !validationError) {
        renameBluePrint();
      }
      onEditClick(null);
    }
  };

  useEffect(() => {
    if (isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing]);

  const isSaveDisabled =
    !!editNameError || !!validationError || editName === blueprintName;

  return (
    <Grid2 className={styles.BlueprintMainContainer}>
      <Grid2
        className={styles.Icon}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {creationInProgress ? (
          <Skeleton
            variant={"rectangular"}
            width={"100%"}
            height={120}
            sx={{
              borderRadius: "8px",
              backgroundColor:
                userDetails?.theme === "dark" ? "#f5f5f5" : "rgba(0,0,0,0.11)",
            }}
          />
        ) : imagePath ? (
          <img
            src={imagePath}
            alt="Blueprint"
            className={styles.uploadedImage}
          />
        ) : (
          <BlueprintSite className={styles.BlueprintIcon} />
        )}
        {isHovered && !creationInProgress && (
          <Grid2 className={styles.IconColor1}>
            <MuiIconButton
              onClick={() => {
                setIsPopupOpen(true);
                setImage(imagePath);
              }}
            >
              <BorderColorOutlinedIcon className={styles.IconColor1} />
            </MuiIconButton>
          </Grid2>
        )}
      </Grid2>
      <Grid2>
        <Grid2 className={styles.siteFlexBox}>
          <Grid2 item className={styles.textBox}>
            {isEditing ? (
              <TextField
                ref={textFieldRef}
                value={editName}
                onChange={handleEditNameChange}
                autoFocus
                size="small"
                error={validationError || !!editNameError}
                helperText={validationError || editNameError}
                inputProps={{ maxLength: maxLength }}
                sx={{
                  "& .MuiOutlinedInput-input": {
                    color:
                      userDetails?.theme === "dark" ? "#ffffff" : "#000000",
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor:
                        userDetails?.theme === "dark" ? "#ffffff" : "#000000",
                    },
                    "&:hover fieldset": {
                      borderColor:
                        userDetails?.theme === "dark" ? "#ffffff" : "#000000",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor:
                        userDetails?.theme === "dark" ? "#ffffff" : "#000000",
                    },
                  },
                }}
              />
            ) : (
              <Tooltip title={editName} arrow>
                <Grid2 className={styles.blueprintName}>{editName}</Grid2>
              </Tooltip>
            )}

            {!isEditing && !creationInProgress && (
              <Tooltip title="Edit" arrow placement="bottom">
                <MuiIconButton
                  onClick={() => {
                    setValidationError("");
                    onEditClick(blueprintId);
                  }}
                >
                  <BorderColorOutlinedIcon className={styles.IconColor} />
                </MuiIconButton>
              </Tooltip>
            )}
          </Grid2>

          {isEditing ? (
            <Grid2
              className={styles.saveButton}
              onClick={handleSaveClick}
              style={{
                opacity: isSaveDisabled ? 0.5 : 1,
                pointerEvents: isSaveDisabled ? "none" : "auto",
              }}
            >
              {loading ? (
                <CircularProgress size={30} color={"inherit"} />
              ) : (
                "Save"
              )}
            </Grid2>
          ) : (
            <Grid2 className={styles.createCount}>
              <DrawOutlinedIcon className={styles.DrawOutlinedIcon} />{" "}
              <Grid2 className={styles.count}>
                {usedCounter ? usedCounter : 0}
              </Grid2>
            </Grid2>
          )}
        </Grid2>

        <Grid2 item className={styles.createdBy}>
          Created By : {name}
        </Grid2>

        {creationInProgress && (
          <Grid2 item className={styles.creationStatus}>
            Creating...
          </Grid2>
        )}
      </Grid2>
      <Grid2 className={styles.siteFlexBox}>
        <Grid2
          item
          className={styles.createSiteFlexBox}
          style={{ opacity: creationInProgress ? 0.5 : 1 }}
          onClick={handleCreateSiteClick}
        >
          <CreateSiteSvg className={styles.icon} />
          <Grid2 item className={styles.createSite}>
            Create Site
          </Grid2>
        </Grid2>
        <IconButton
          icon={Delete}
          tooltipText="Delete"
          onClick={() => {
            if (creationInProgress) {
              ActivatePopup("blueprint-creation-in-progress", setPopup);
              return;
            }
            deleteBlueprint();
          }}
        />
      </Grid2>
      <NewPopup
        trigger={isPopupOpen}
        closePopup={() => setIsPopupOpen(false)}
        header={"Edit Blueprint"}
        backPopup="Cancel"
        backOnClick={() => {
          setIsPopupOpen(false);
        }}
        nextpopup={"Save"}
        nextOnClick={apiImageReUpload}
        loading={loading}
        disabled={isUpload}
      >
        <ImageUploader
          imagePreview={image}
          onDelete={apiDeleteImage}
          onUploadClick={() => fileInputRef.current.click()}
          onFileChange={handleFileChange}
          fileInputRef={fileInputRef}
          loading={loadingDel}
        />
      </NewPopup>
      <FormPopup
        trigger={popup.popupState}
        setTrigger={setPopup}
        type={popup.popupType}
        title={popup.popupTitle}
        subtitle={popup.popupSubtitle}
        button={popup.popupButton}
        cancel={popup.popupCancel}
        buttonTitle={popup.popupButtonTitle}
        buttonFunction={popup.popupButtonFunction}
        cancelTitle={popup.popupCancelTitle}
        cancelFunction={popup.popupCancelFunction}
      />
    </Grid2>
  );
}

BlueprintCard.propTypes = {
  blueprintName: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired, // SvgIconComponent
  name: PropTypes.string.isRequired,
};
